// src/views/sunscreens/admin/webhooks/AddWebhook.js
import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Modal,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/styles";
import { useTranslation } from "react-i18next";
import { getAllAlarms } from "src/repos/alarms";
import { getAllMaintenances } from "src/repos/maintenances";
import { getCustomers } from "src/repos/customers";

const useStyles = makeStyles((theme) => ({
  modalBody: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    position: "absolute",
    outline: "none",
    width: "30%",
    [theme.breakpoints.up("lg")]: { width: "40%" },
    [theme.breakpoints.down("md")]: { width: "65%" },
    [theme.breakpoints.down("xs")]: { width: "90%" },
  },
}));

function AddWebhook({ open, webhook, onClose, onCreate, onUpdate, onDelete }) {
  const classes = useStyles();
  const { t } = useTranslation(["glossary", "common"]);
  const isNew = !webhook;

  const [webhookName, setWebhookName] = useState("");
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [authType, setAuthType] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [tokenUrl, setTokenUrl] = useState("");
  const [customFields, setCustomFields] = useState("");
  const [active, setActive] = useState(true);

  const [customersList, setCustomersList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [alarmsList, setAlarmsList] = useState([]);
  const [selectedAlarms, setSelectedAlarms] = useState([]);
  const [maintenancesList, setMaintenancesList] = useState([]);
  const [selectedMaintenances, setSelectedMaintenances] = useState([]);
  const [loadingAlarms, setLoadingAlarms] = useState(false);
  const [loadingMaintenances, setLoadingMaintenances] = useState(false);
  const [loadError, setLoadError] = useState({
    alarms: false,
    maintenances: false,
  });

  const validateUrl = (value) => {
    if (!value) return "URL is required";
    if (!/^https?:\/\/.+/.test(value))
      return "URL must start with http:// or https://";
    return "";
  };

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    setUrlError(validateUrl(newUrl));
  };

  const getAlarmMachineName = (alarm) => {
    if (alarm.device_name) return alarm.device_name;
    if (alarm.device && alarm.device.name) return alarm.device.name;
    return "Unknown Machine";
  };

  const getMaintenanceMachineName = (maintenance) => {
    return (
      maintenance.device_name ||
      (maintenance.device && maintenance.device.name) ||
      "Unknown Machine"
    );
  };

  const getAlarmLabel = (alarm) => {
    const machine = getAlarmMachineName(alarm);
    return `${alarm.name} (${machine})`;
  };

  const getMaintenanceLabel = (maintenance) => {
    const machine = getMaintenanceMachineName(maintenance);
    return `${maintenance.name} (${machine})`;
  };

  useEffect(() => {
    if (open) {
      getCustomers()
        .then((r) => r.json())
        .then(({ data }) => setCustomersList(data || []))
        .catch(console.error);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedCustomer) {
      setAlarmsList([]);
      setMaintenancesList([]);
      setSelectedAlarms([]);
      setSelectedMaintenances([]);
      return;
    }

    const customerId = selectedCustomer.id;

    setLoadingAlarms(true);
    setLoadError((prev) => ({ ...prev, alarms: false }));
    getAllAlarms({ customer_id: customerId })
      .then((r) => r.json())
      .then((response) => {
        const alarms =
          response.alarms ||
          response.data ||
          (Array.isArray(response) ? response : []);
        setAlarmsList(alarms);
      })
      .catch(() => setLoadError((prev) => ({ ...prev, alarms: true })))
      .finally(() => setLoadingAlarms(false));

    setLoadingMaintenances(true);
    setLoadError((prev) => ({ ...prev, maintenances: false }));
    getAllMaintenances({ customer_id: customerId })
      .then((r) => r.json())
      .then((response) => {
        const maintenances =
          response.maintenances ||
          response.data ||
          (Array.isArray(response) ? response : []);
        setMaintenancesList(maintenances);
      })
      .catch(() => setLoadError((prev) => ({ ...prev, maintenances: true })))
      .finally(() => setLoadingMaintenances(false));
  }, [selectedCustomer]);

  useEffect(() => {
    if (webhook) {
      setWebhookName(webhook.webhook_name || "");
      setUrl(webhook.url || "");
      setUrlError(validateUrl(webhook.url || ""));
      setAuthType(webhook.auth_type || "");
      setClientId(webhook.client_id || "");
      setClientSecret(webhook.client_secret || "");
      setTokenUrl(webhook.token_url || "");
      setCustomFields(
        webhook.custom_fields
          ? JSON.stringify(webhook.custom_fields, null, 2)
          : ""
      );
      setActive(webhook.active !== undefined ? webhook.active : true);
      setSelectedCustomer(webhook.customer || null);
      setSelectedAlarms(webhook.alarms || []);
      setSelectedMaintenances(webhook.maintenances || []);
    } else {
      setWebhookName("");
      setUrl("");
      setUrlError("");
      setAuthType("");
      setClientId("");
      setClientSecret("");
      setTokenUrl("");
      setCustomFields("");
      setActive(true);
      setSelectedCustomer(null);
      setSelectedAlarms([]);
      setSelectedMaintenances([]);
    }
  }, [webhook, open]);

  const submit = () => {
    if (validateUrl(url)) {
      setUrlError(validateUrl(url));
      return;
    }
    let parsedCustomFields = null;
    if (customFields.trim()) {
      try {
        parsedCustomFields = JSON.parse(customFields);
      } catch (e) {
        return;
      }
    }
    const payload = {
      webhook_name: webhookName,
      url,
      auth_type: authType || null,
      client_id: clientId || null,
      client_secret: clientSecret || null,
      token_url: tokenUrl || null,
      custom_fields: parsedCustomFields,
      active,
      customer_id: selectedCustomer.id,
      alarm_ids: selectedAlarms.map((a) => a.id),
      maintenance_ids: selectedMaintenances.map((m) => m.id),
    };
    if (isNew) onCreate(payload);
    else onUpdate({ ...payload, id: webhook.id });
    onClose();
  };

  const isValid = () => {
    if (!webhookName || !url || !selectedCustomer) return false;
    if (urlError) return false;
    if (authType === "oauth2" && (!clientId || !clientSecret || !tokenUrl))
      return false;
    if (authType === "bearer" && !clientSecret) return false;
    return true;
  };

  return (
    <Modal open={open} onClose={onClose} style={{ overflow: "scroll" }}>
      <div className={classes.modalBody}>
        <Card>
          <CardHeader
            title={
              <span style={{ fontSize: "20px" }}>
                {isNew ? t("add_webhook") : t("edit_webhook")}
              </span>
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("name")}
                  variant="outlined"
                  value={webhookName}
                  onChange={(e) => setWebhookName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={customersList}
                  getOptionLabel={(option) => option.name}
                  value={selectedCustomer}
                  onChange={(e, v) => setSelectedCustomer(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Customer"
                      placeholder="Select a customer"
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("url")}
                  variant="outlined"
                  value={url}
                  onChange={handleUrlChange}
                  error={!!urlError}
                  helperText={urlError || "Must start with http:// or https://"}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{t("auth_type")}</InputLabel>
                  <Select
                    value={authType}
                    onChange={(e) => setAuthType(e.target.value)}
                    label={t("auth_type")}
                  >
                    <MenuItem value="">{t("none")}</MenuItem>
                    <MenuItem value="bearer">Bearer Token</MenuItem>
                    <MenuItem value="oauth2">OAuth2</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {authType === "bearer" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("client_secret")}
                    variant="outlined"
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    required
                  />
                </Grid>
              )}
              {authType === "oauth2" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("client_id")}
                      variant="outlined"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("client_secret")}
                      variant="outlined"
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("token_url")}
                      variant="outlined"
                      value={tokenUrl}
                      onChange={(e) => setTokenUrl(e.target.value)}
                      required
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("custom_fields")}
                  variant="outlined"
                  multiline
                  rows={4}
                  value={customFields}
                  onChange={(e) => setCustomFields(e.target.value)}
                  helperText={t("custom_fields_helper")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t("active")}
                />
              </Grid>

              <Grid item xs={12}>
                {loadingAlarms ? (
                  <CircularProgress size={24} />
                ) : loadError.alarms ? (
                  <Typography color="error">Failed to load alarms</Typography>
                ) : (
                  <Autocomplete
                    multiple
                    options={alarmsList}
                    getOptionLabel={(option) => getAlarmLabel(option)}
                    groupBy={(option) => getAlarmMachineName(option)}
                    value={selectedAlarms}
                    onChange={(e, v) => setSelectedAlarms(v)}
                    disabled={!selectedCustomer}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Associated Alarms"
                        placeholder={
                          selectedCustomer
                            ? "Select alarms"
                            : "Select a customer first"
                        }
                      />
                    )}
                  />
                )}
              </Grid>

              {/* Maintenances dropdown with grouping by machine */}
              <Grid item xs={12}>
                {loadingMaintenances ? (
                  <CircularProgress size={24} />
                ) : loadError.maintenances ? (
                  <Typography color="error">
                    Failed to load maintenances
                  </Typography>
                ) : (
                  <Autocomplete
                    multiple
                    options={maintenancesList}
                    getOptionLabel={(option) => getMaintenanceLabel(option)}
                    groupBy={(option) => getMaintenanceMachineName(option)}
                    value={selectedMaintenances}
                    onChange={(e, v) => setSelectedMaintenances(v)}
                    disabled={!selectedCustomer}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Associated Maintenances"
                        placeholder={
                          selectedCustomer
                            ? "Select maintenances"
                            : "Select a customer first"
                        }
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions style={{ justifyContent: "space-between" }}>
            <span>
              <Button
                disabled={!isValid()}
                variant="contained"
                color="secondary"
                onClick={submit}
              >
                {t("submit")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={onClose}
                style={{ marginLeft: "5px" }}
              >
                {t("close")}
              </Button>
            </span>
            {!isNew && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  onDelete(webhook);
                  onClose();
                }}
              >
                {t("delete")}
              </Button>
            )}
          </CardActions>
        </Card>
      </div>
    </Modal>
  );
}

export default AddWebhook;
